import { getPartnerLanguages } from '@/api/business-profile/getLanguages';
import { GetPartnerLanguagesResponse, LANG_TYPE } from '@/api/type/types';
import { useLoading } from '@/context/LoadingContext';
import { useAppSelector } from '@/stores/hooks';
import { businessProfile } from '@/stores/slices/business-profile/businessProfileSlice';
import { fonts } from '@/theme/fonts';
import { getFileUrl } from '@/utils/file/getFileUrl';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

interface ProfileLanguagesProps {
  data: LANG_TYPE[];
  isEdit: boolean;
}
const ProfileLanguages = ({ data, isEdit }: ProfileLanguagesProps) => {
  const [langData, setLangData] = useState<GetPartnerLanguagesResponse[]>([]);
  const [addedLangs, setAddedLangs] = useState<LANG_TYPE[]>([]);
  const { startLoading, stopLoading } = useLoading();
  const dispatch = useDispatch();
  const {
    putBusinessProfile,
    lagnModalOpen,
    businessProfile: originData,
  } = useAppSelector((state) => state.businessProfile);

  const onClickAddBtn = useCallback(async () => {
    dispatch(businessProfile.setLangModalOpen(!lagnModalOpen));
    startLoading();
    try {
      const res = await getPartnerLanguages();
      if (res.isSuccess) {
        setLangData(res.data);
      }
    } finally {
      stopLoading();
    }
  }, [dispatch, lagnModalOpen, startLoading, stopLoading]);

  const isSelectedLang = useCallback(
    (id: string) => {
      if (isEdit) {
        return putBusinessProfile?.languages.some((l) => l.id === id) ?? false;
      }

      return originData?.languages.some((l) => l.id === id) ?? false;
    },
    [isEdit, originData?.languages, putBusinessProfile?.languages]
  );

  const displayLangs = useMemo(() => {
    const map = new Map<string, LANG_TYPE>();

    data.forEach((d) => map.set(d.id, d));
    addedLangs.forEach((d) => map.set(d.id, d));

    const langs = Array.from(map.values());
    if (isEdit) return langs; // 편집 중이면 전부 노출(제거했을 땐 하얀색 배경)
    return langs.filter((lang) => isSelectedLang(lang.id)); // 편집 아닐 때는 선택된 데이터만
  }, [data, addedLangs, isEdit, isSelectedLang]);

  const onToggleLang = useCallback(
    (id: string, name: string) => () => {
      if (!isEdit) return;
      const prev = putBusinessProfile?.languages ?? [];
      const exists = prev.some((l) => l.id === id);

      const updated = exists
        ? prev.filter((l) => l.id !== id)
        : [...prev, { id, name }];
      dispatch(businessProfile.putBusinessProfile({ languages: updated }));
      dispatch(businessProfile.setLangModalOpen(false));
      if (
        !data.find((d) => d.id === id) &&
        !addedLangs.find((d) => d.id === id)
      ) {
        setAddedLangs((prev) => [...prev, { id, name }]);
      }
    },
    [isEdit, putBusinessProfile?.languages, dispatch, data, addedLangs]
  );

  return (
    <LangWrap>
      {displayLangs.map((item) => (
        <Lang
          key={item.id}
          data-edit={isEdit}
          data-selected={isSelectedLang(item.id)}
          onClick={onToggleLang(item.id, item.name)}
        >
          {item.name}
        </Lang>
      ))}

      {isEdit && (
        <LangContainer>
          <AddButton onClick={onClickAddBtn}>+</AddButton>
          {lagnModalOpen && (
            <LangBox>
              {langData.map((item) => (
                <LangItem
                  key={item.id}
                  data-selected={isSelectedLang(item.id)}
                  onClick={onToggleLang(item.id, item.name)}
                >
                  <LangImg
                    src={getFileUrl(item.countryImageId)}
                    alt={`${item.id}-img`}
                  />
                  <div>{item.name}</div>
                </LangItem>
              ))}
            </LangBox>
          )}
        </LangContainer>
      )}
    </LangWrap>
  );
};

export default ProfileLanguages;

const LangWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  flex-wrap: wrap;
`;

const Lang = styled.button`
  font-size: 15px;
  line-height: 140%;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 50px;
  background: #ffffff;
  color: #000000;
  border-radius: 15px;
  border: 1px solid #ababab;
  cursor: auto;
  flex-shrink: 0;
  &[data-edit='true'] {
    cursor: pointer;
  }
  &[data-edit='true'][data-selected='true'] {
    background: #64a9fe;
    color: #fffcfc;
  }
`;

const LangContainer = styled.div`
  position: relative;
`;

const AddButton = styled.button`
  width: 87px;
  height: 40px;
  border: 1px solid #ababab;
  background: #ffffff;
  border-radius: 15px;
  font-family: ${fonts.family.inter};
  font-size: 25px;
  color: #000000;
  display: flex;
  justify-content: center;
`;

const LangBox = styled.div`
  position: absolute;
  top: 50px;
  left: 0;
  background: #ffffff;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  padding: 20px;
  column-gap: 40px;
  row-gap: 20px;
  box-shadow: 0px 0px 20px rgba(21, 17, 49, 0.2);
  border-radius: 10px;
`;

const LangItem = styled.button`
  display: flex;
  align-items: center;
  gap: 15px;
  font-size: 14px;
  line-height: 140%;
  color: #000000;
  padding: 10px 15px;
  border-radius: 10px;
  &:hover {
    background: rgba(140, 162, 244, 0.5);
  }
`;

const LangImg = styled.img`
  width: 32px;
  height: 20px;
`;
